import { useEffect, useCallback } from "react";
import { useAuth } from "../context/context";

/**
 * Hook para manejar la validación automática de tokens y manejo de sesiones
 */
export const useTokenValidation = () => {
  const { token, logout, checkTokenValidity, isValidatingToken } = useAuth();

  // Función para interceptar respuestas HTTP y manejar tokens expirados
  const handleApiResponse = useCallback(
    async (response) => {
      if (response.status === 401 || response.status === 403) {
        console.warn("🔐 Token expirado detectado en respuesta API");
        logout();
        return false;
      }
      return true;
    },
    [logout]
  );

  // Función para crear un interceptor de fetch que valide automáticamente los tokens
  const createAuthenticatedFetch = useCallback(
    (originalFetch = fetch) => {
      return async (url, options = {}) => {
        // Si hay token, agregarlo a los headers
        if (token && options.headers) {
          options.headers = {
            ...options.headers,
            Authorization: `Bearer ${token}`,
          };
        }

        try {
          const response = await originalFetch(url, options);

          // Manejar respuestas de token expirado
          await handleApiResponse(response);

          return response;
        } catch (error) {
          console.error("Error en fetch autenticado:", error);
          throw error;
        }
      };
    },
    [token, handleApiResponse]
  );

  // Función para validar token antes de operaciones críticas
  const ensureValidToken = useCallback(async () => {
    if (!token) {
      throw new Error("No hay token disponible");
    }

    const isValid = await checkTokenValidity();
    if (!isValid) {
      throw new Error("Token expirado");
    }

    return true;
  }, [token, checkTokenValidity]);

  return {
    isValidatingToken,
    handleApiResponse,
    createAuthenticatedFetch,
    ensureValidToken,
    hasToken: !!token,
  };
};
